// useVoiceSession — "Scan & Talk" voice mode for a public form, on
// GPT-Live (gpt-live-1).
//
// Flow (browser-held WebRTC, server-created session — see docs/GPT_LIVE.md):
//   1. POST /api/voice/session {formId, idToken, clientSurface} ->
//      {voiceSessionId, maxSeconds, fields, formMeta, model, transport}.
//   2. Open a WebRTC PeerConnection, create the 'oai-events' data channel and
//      an SDP offer, then POST /api/voice/webrtc {voiceSessionId, sdp}. The
//      server creates the GPT-Live session with the project key and returns
//      the SDP answer — the browser never holds an OpenAI credential.
//   3. GPT-Live delegates every action to a backend model. Its function calls
//      arrive on the data channel as `response.event` envelopes; each one is
//      relayed to POST /api/voice/tool {voiceSessionId, name, arguments} and
//      the result goes back as response.item.create + response.create.
//   4. Submission only ever happens via POST /api/voice/submit, triggered by a
//      human tap on the review overlay (there is no submit tool).
//
// Remove this file + VoicePanel + the mode chooser in FormChatScreen to fully
// undo web voice.

import { useCallback, useEffect, useRef, useState } from 'react';

import AuthService from '../services/AuthService';
import { uploadFormImage } from '../services/ImageUploadService';

const DEFAULT_DISCLOSURE = 'This call is recorded for the business.';
// A pause longer than this between transcript fragments starts a new line
// (mirrors live_engine.TRANSCRIPT_GAP_MS so every surface segments alike).
const TRANSCRIPT_GAP_MS = 1500;

export type VoiceStatus = 'idle' | 'connecting' | 'live' | 'ended' | 'error';

export interface VoiceField {
  name: string;
  type: string;
  optional: boolean;
}

export interface VoiceDraft {
  [name: string]: string;
}

export interface TranscriptEntry {
  id: string;
  role: 'agent' | 'customer' | 'system';
  text: string;
}

export interface VoiceFormMeta {
  id: string;
  title: string;
  /** Business (channel) display name. */
  businessName?: string;
  channelUid?: string;
  voiceDisclosure?: string;
  formImageUrl?: string;
  imageUrls?: string[];
  introMessage?: string;
  /** Agent can book appointments — the call stays open after submit. */
  canBook?: boolean;
  /** Agent has handoff targets — the call stays open after submit. */
  canTransfer?: boolean;
}

export interface SubmitOutcome {
  ok: boolean;
  error?: string;
}

/** What the session is keyed to. */
export interface VoiceTarget {
  formId?: string | null;
}

export interface UseVoiceSession {
  status: VoiceStatus;
  error: string | null;
  fields: VoiceField[];
  draft: VoiceDraft;
  transcript: TranscriptEntry[];
  /** Draft snapshot when the agent opened the review overlay; null when closed. */
  reviewDraft: VoiceDraft | null;
  submitted: boolean;
  /** Submitted, but the call stays open so the agent can book an appointment
      (formMeta.canBook / submit response keepOpen) — no finished card. */
  keepOpen: boolean;
  formMeta: VoiceFormMeta | null;
  /** Link button offered by the agent — another form (show_form_link) or, with
   *  external=true, a business web page (show_link) that opens in a new tab
   *  without ending the call. Null when none. */
  linkOffer: { url: string; title: string; external?: boolean } | null;
  /** Image the agent chose to show (show_image) — overlays the call UI. */
  shownImage: string | null;
  closeShownImage: () => void;
  /** Consent caption to show whenever the mic is live. */
  disclosure: string;
  /** The agent called request_photo and is waiting on a photo (spec §5) —
      mic is muted; VoicePanel should show the camera sheet + "Mic paused"
      chip while this is set. */
  cameraRequest: { fieldName: string } | null;
  /** Photo picked for the pending camera request: uploads, POSTs
      /api/voice/photo, and only then releases the deferred
      function_call_output back to the model. */
  completePhotoCapture: (file: File) => Promise<SubmitOutcome>;
  /** Customer closed the camera sheet without taking a photo. */
  cancelPhotoCapture: () => void;
  /** The backend is working on a delegated request (tool call in flight). */
  thinking: boolean;
  start: () => void;
  stop: () => void;
  submitReview: (edits: VoiceDraft) => Promise<SubmitOutcome>;
  dismissReview: () => void;
}

interface SessionResponse {
  voiceSessionId: string;
  transport?: string;
  webrtcUrl?: string;
  model: string;
  maxSeconds?: number;
  fields?: VoiceField[];
  formMeta?: VoiceFormMeta;
}

interface SpeakerState {
  lineId: string | null;
  lastEndMs: number | null;
}

export function useVoiceSession(
  target: VoiceTarget,
  userId: string | null | undefined,
): UseVoiceSession {
  const { formId } = target;
  const [status, setStatus] = useState<VoiceStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const [fields, setFields] = useState<VoiceField[]>([]);
  const [draft, setDraft] = useState<VoiceDraft>({});
  const [transcript, setTranscript] = useState<TranscriptEntry[]>([]);
  const [reviewDraft, setReviewDraft] = useState<VoiceDraft | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [keepOpen, setKeepOpen] = useState(false);
  const [formMeta, setFormMeta] = useState<VoiceFormMeta | null>(null);
  const [linkOffer, setLinkOffer] = useState<{ url: string; title: string; external?: boolean } | null>(null);
  const [shownImage, setShownImage] = useState<string | null>(null);
  const [cameraRequest, setCameraRequest] = useState<{ fieldName: string } | null>(null);
  const [thinking, setThinking] = useState(false);

  const pcRef = useRef<RTCPeerConnection | null>(null);
  const dcRef = useRef<RTCDataChannel | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const timerRef = useRef<number | null>(null);
  const sessionIdRef = useRef<string | null>(null);
  const startingRef = useRef(false);
  const noteSeqRef = useRef(0);
  const delegationsRef = useRef(0);
  const speakersRef = useRef<{ agent: SpeakerState; customer: SpeakerState }>({
    agent: { lineId: null, lastEndMs: null },
    customer: { lineId: null, lastEndMs: null },
  });
  // The request_photo call awaiting a deferred function_call_output (spec §5)
  // — held here (not just in state) so complete/cancel can reach it
  // synchronously without a stale closure.
  const pendingPhotoRef = useRef<{ callId: string; fieldName: string } | null>(null);
  // Session generation: bumped by every start() and cleanup() so async
  // continuations from a stopped session can detect they were cancelled and
  // release anything they created instead of resurrecting the call.
  const genRef = useRef(0);

  const cleanup = useCallback(() => {
    genRef.current += 1;
    try { dcRef.current?.close(); } catch { /* noop */ }
    try { pcRef.current?.close(); } catch { /* noop */ }
    streamRef.current?.getTracks().forEach((t) => t.stop());
    if (audioRef.current) {
      audioRef.current.srcObject = null;
      audioRef.current.remove();
    }
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    dcRef.current = null;
    pcRef.current = null;
    streamRef.current = null;
    audioRef.current = null;
    startingRef.current = false;
    pendingPhotoRef.current = null;
    delegationsRef.current = 0;
    speakersRef.current = {
      agent: { lineId: null, lastEndMs: null },
      customer: { lineId: null, lastEndMs: null },
    };
    setCameraRequest(null);
    setThinking(false);
  }, []);

  // Tear everything down when the owning view unmounts.
  useEffect(() => cleanup, [cleanup]);

  // ---- data-channel senders -------------------------------------------------

  const sendEvent = useCallback((event: any) => {
    const dc = dcRef.current;
    if (!dc || dc.readyState !== 'open') return false;
    try {
      dc.send(JSON.stringify(event));
      return true;
    } catch {
      return false;
    }
  }, []);

  const stop = useCallback(() => {
    // Ask GPT-Live to close first so usage is finalized server-side; the
    // peer connection goes down right after regardless.
    sendEvent({ type: 'session.close' });
    const sid = sessionIdRef.current;
    if (sid) {
      fetch('/api/voice/end', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ voiceSessionId: sid }),
        keepalive: true,
      }).catch(() => { /* best-effort */ });
    }
    cleanup();
    setStatus((s) => (s === 'error' || s === 'idle' ? s : 'ended'));
  }, [cleanup, sendEvent]);
  const stopRef = useRef(stop);
  stopRef.current = stop;

  // ---- mic mute (request_photo camera sheet) --------------------------------

  /** Enables/disables the local mic track in place — used to pause the mic
      while the camera sheet is up for a request_photo call (spec §5.2). */
  const setMicEnabled = useCallback((enabled: boolean) => {
    streamRef.current?.getAudioTracks().forEach((track) => {
      track.enabled = enabled;
    });
    sendEvent({ type: enabled ? 'session.input_audio.unmute' : 'session.input_audio.mute' });
  }, [sendEvent]);

  /** Brief the live model about something that happened on screen. GPT-Live
      has no user-message injection; a session-wide instructions.append is
      the documented way to steer it mid-conversation. */
  const sendScreenEvent = useCallback(
    (text: string) => {
      sendEvent({ type: 'session.instructions.append', delegation_id: null, content: text });
    },
    [sendEvent],
  );

  /** Return a tool result to the backend model and let it continue. */
  const sendFunctionOutput = useCallback(
    (callId: string, output: any) => {
      sendEvent({
        type: 'response.item.create',
        item: { type: 'function_call_output', call_id: callId, output: JSON.stringify(output) },
      });
      sendEvent({ type: 'response.create' });
    },
    [sendEvent],
  );

  // ---- transcript helpers ---------------------------------------------------

  const upsertLine = useCallback((id: string, role: 'agent' | 'customer', apply: (prev: string) => string) => {
    setTranscript((prev) => {
      const idx = prev.findIndex((e) => e.id === id);
      if (idx === -1) return [...prev, { id, role, text: apply('') }];
      const next = prev.slice();
      next[idx] = { ...next[idx], text: apply(next[idx].text) };
      return next;
    });
  }, []);

  const addLine = useCallback((role: 'customer' | 'system', text: string) => {
    noteSeqRef.current += 1;
    const id = `${role}-${noteSeqRef.current}`;
    setTranscript((prev) => [...prev, { id, role, text }]);
  }, []);

  /** GPT-Live streams transcript fragments with start/end timestamps and no
      turn boundaries; a pause longer than TRANSCRIPT_GAP_MS starts a new line. */
  const applyTranscriptDelta = useCallback(
    (role: 'agent' | 'customer', delta: string, startMs?: number, endMs?: number) => {
      if (!delta) return;
      const sp = speakersRef.current[role];
      const gap = sp.lineId && sp.lastEndMs !== null && typeof startMs === 'number'
        ? startMs - sp.lastEndMs
        : 0;
      if (!sp.lineId || gap > TRANSCRIPT_GAP_MS) {
        noteSeqRef.current += 1;
        sp.lineId = `${role}-${noteSeqRef.current}`;
      }
      if (typeof endMs === 'number') sp.lastEndMs = endMs;
      upsertLine(sp.lineId, role, (prev) => prev + delta);
    },
    [upsertLine],
  );

  // ---- tool round trip ------------------------------------------------------

  const handleFunctionCall = useCallback(
    async (name: string, callId: string, args: any) => {
      let toolResponse: any;
      try {
        const resp = await fetch('/api/voice/tool', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            voiceSessionId: sessionIdRef.current,
            name,
            arguments: args ?? {},
          }),
        });
        toolResponse = await resp.json();
      } catch (e: any) {
        toolResponse = {
          ok: false,
          error: 'The tool call failed — apologize briefly and try again.',
        };
      }

      const result = toolResponse?.result;
      if (toolResponse?.ok && result) {
        // capture_field / open_review echo the authoritative server draft.
        if (result.draft && typeof result.draft === 'object') {
          setDraft({ ...result.draft });
        }
        if (name === 'open_review' && result.show_review) {
          setReviewDraft({ ...(result.draft || {}) });
        }
        if (name === 'transfer_form' && result.status === 'transfer') {
          // Only the backend block can change mid-session; the live model is
          // re-briefed with the short instructions.append the server built.
          if (result.live_update) sendEvent({ type: 'session.update', session: result.live_update });
          if (result.live_brief) sendScreenEvent(result.live_brief);
          if (Array.isArray(result.fields)) setFields(result.fields);
          // Fresh agent: a new submit is possible; seed pre-filled answers.
          setDraft(result.draft && typeof result.draft === 'object' ? { ...result.draft } : {});
          setSubmitted(false);
          setKeepOpen(false);
          setReviewDraft(null);
          setLinkOffer(null);
          setShownImage(null);
          if (result.formMeta) {
            setFormMeta(result.formMeta);
            addLine('system', `→ switched to ${result.formMeta.title}`);
          }
        }
        if (name === 'show_form_link' && result.show_link && result.url) {
          setLinkOffer({ url: result.url, title: result.title || 'the other form' });
        }
        if (name === 'show_link' && result.show_link && result.url) {
          // Business web page — opens in a new tab; the call keeps running.
          setLinkOffer({ url: result.url, title: result.title || 'Open link', external: true });
        }
        if (name === 'show_image' && result.show_image && result.url) {
          setShownImage(result.url);
        }
        if (name === 'book_appointment' && (result.status === 'appointment_requested' || result.status === 'booked')) {
          addLine('system', result.status === 'booked' ? 'Appointment booked ✓' : 'Appointment requested ✓');
        }
        if (name === 'request_photo' && result.show_camera) {
          // Deferred output (spec §5): mute the mic and open the camera sheet;
          // do NOT send the function output until the photo is uploaded (or
          // the customer cancels) — completePhotoCapture/cancelPhotoCapture
          // send it later.
          pendingPhotoRef.current = { callId, fieldName: result.field_name };
          setMicEnabled(false);
          setCameraRequest({ fieldName: result.field_name });
          return;
        }
      }

      // The backend never sees the transfer session payload — it only needs
      // to know the handoff happened.
      const output = name === 'transfer_form' && toolResponse?.ok && result
        ? { ok: true, result: { status: 'transfer', formId: result.formId, fields: result.fields, draft: result.draft } }
        : toolResponse;
      sendFunctionOutput(callId, output);
    },
    [sendEvent, sendScreenEvent, sendFunctionOutput, addLine, setMicEnabled],
  );

  // ---- request_photo camera sheet actions (human taps) -----------------------

  const completePhotoCapture = useCallback(
    async (file: File): Promise<SubmitOutcome> => {
      const pending = pendingPhotoRef.current;
      if (!pending) return { ok: false, error: 'No pending photo request.' };

      const finish = (output: any) => {
        pendingPhotoRef.current = null;
        setCameraRequest(null);
        setMicEnabled(true);
        sendFunctionOutput(pending.callId, output);
      };

      try {
        const url = await uploadFormImage({
          file,
          channelUid: formMeta?.channelUid || '',
          formId: formId || '',
        });
        const resp = await fetch('/api/voice/photo', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            voiceSessionId: sessionIdRef.current,
            field_name: pending.fieldName,
            url,
          }),
        });
        const data = await resp.json().catch(() => ({ ok: false }));

        if (data?.ok) {
          if (data.result?.draft && typeof data.result.draft === 'object') {
            setDraft({ ...data.result.draft });
          }
          finish({ status: 'photo_received', field_name: pending.fieldName });
          return { ok: true };
        }

        // ok:false — feed it back immediately like any other tool error
        // (spec §5.5) so the model can recover out loud.
        finish(data);
        return { ok: false, error: data?.error };
      } catch (e: any) {
        const message = e?.message || 'Upload failed — please try again.';
        finish({ ok: false, error: message });
        return { ok: false, error: message };
      }
    },
    [formId, formMeta, sendFunctionOutput, setMicEnabled],
  );

  const cancelPhotoCapture = useCallback(() => {
    const pending = pendingPhotoRef.current;
    if (!pending) return;
    pendingPhotoRef.current = null;
    setCameraRequest(null);
    setMicEnabled(true);
    sendFunctionOutput(pending.callId, {
      status: 'cancelled',
      note:
        'The customer closed the camera without taking a photo — ask if they want to try again or continue.',
    });
  }, [sendFunctionOutput, setMicEnabled]);

  // ---- GPT-Live event routing (unknown events are ignored, never fatal) -----

  const onDataChannelMessage = useCallback(
    (ev: MessageEvent) => {
      let msg: any;
      try { msg = JSON.parse(ev.data); } catch { return; }
      if (!msg || typeof msg.type !== 'string') return;

      switch (msg.type) {
        case 'session.started':
          setStatus('live');
          break;

        // Agent / customer speech, as timestamped fragments.
        case 'session.output_transcript.delta':
          applyTranscriptDelta('agent', msg.delta, msg.start_ms, msg.end_ms);
          break;
        case 'session.input_transcript.delta':
          applyTranscriptDelta('customer', msg.delta, msg.start_ms, msg.end_ms);
          break;

        // The live model handed something to the backend.
        case 'session.delegation.created':
          delegationsRef.current += 1;
          setThinking(true);
          break;

        // Backend (Responses) events, nested. Completed function calls carry
        // the tool name + arguments; a finished response clears "thinking".
        case 'response.event': {
          const inner = msg.event || {};
          if (inner.type === 'response.output_item.done' && inner.item?.type === 'function_call') {
            let parsedArgs: any = {};
            try { parsedArgs = inner.item.arguments ? JSON.parse(inner.item.arguments) : {}; } catch { /* keep {} */ }
            if (inner.item.name && inner.item.call_id) {
              handleFunctionCall(inner.item.name, inner.item.call_id, parsedArgs);
            }
          } else if (
            inner.type === 'response.completed'
            || inner.type === 'response.failed'
            || inner.type === 'response.incomplete'
          ) {
            delegationsRef.current = Math.max(0, delegationsRef.current - 1);
            if (delegationsRef.current === 0) setThinking(false);
          }
          break;
        }

        case 'session.closed':
          // OpenAI ended the session (cap, moderation, or our own close).
          cleanup();
          setStatus((s) => (s === 'error' || s === 'idle' ? s : 'ended'));
          break;

        case 'error':
          // Non-fatal by contract (rejected command, moderation interrupt).
          // eslint-disable-next-line no-console
          console.warn('[voice] GPT-Live error', msg.error);
          break;

        default:
          break;
      }
    },
    [applyTranscriptDelta, handleFunctionCall, cleanup],
  );

  // ---- review overlay actions (human taps) ----------------------------------

  const submitReview = useCallback(
    async (edits: VoiceDraft): Promise<SubmitOutcome> => {
      const sid = sessionIdRef.current;
      if (!sid) return { ok: false, error: 'No active voice session.' };
      try {
        const resp = await fetch('/api/voice/submit', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ voiceSessionId: sid, edits: edits || {} }),
        });
        if (resp.ok) {
          setDraft((prev) => ({ ...prev, ...edits }));
          const body = await resp.json().catch(() => ({} as any));
          const stayOpen = body?.keepOpen === true || formMeta?.canBook === true;
          const canBook = formMeta?.canBook === true;
          setSubmitted(true);
          setKeepOpen(stayOpen);
          setReviewDraft(null);
          addLine('system', 'Submitted ✓');
          sendScreenEvent(
            stayOpen && canBook
              ? 'The customer reviewed their answers on screen and tapped Submit. It is submitted. If they want an appointment, offer to book it now; otherwise wrap up warmly.'
              : stayOpen
              ? 'The customer reviewed their answers on screen and tapped Submit. It is submitted. Ask if there is anything else they need. If it belongs to another agent, transfer them; otherwise wrap up warmly and say goodbye.'
              : 'The customer reviewed their answers on screen and tapped Submit. It is submitted. Wrap up warmly and say goodbye.',
          );
          return { ok: true };
        }

        const detail = await resp.json().catch(() => ({} as any));
        const serverError: string | undefined =
          typeof detail?.error === 'string' ? detail.error : undefined;

        if (resp.status === 422) {
          // Required fields still missing — hand it back to the agent.
          setReviewDraft(null);
          sendScreenEvent(
            `The customer tapped Submit but required information is still missing${
              serverError ? ` (${serverError})` : ''
            }. Ask for the missing details; the backend will reopen the review.`,
          );
          return {
            ok: false,
            error: serverError || 'Some required answers are still missing.',
          };
        }
        if (resp.status === 409) {
          // Already submitted — treat as success for the customer.
          setSubmitted(true);
          setKeepOpen(formMeta?.canBook === true);
          setReviewDraft(null);
          return { ok: true };
        }
        if (resp.status === 410) {
          // Voice session expired server-side — nothing left to submit against.
          return {
            ok: false,
            error: 'This voice session has expired — please hang up and start a new call.',
          };
        }
        return { ok: false, error: serverError || `Submit failed (${resp.status}).` };
      } catch (e: any) {
        return { ok: false, error: e?.message || 'Submit failed — please try again.' };
      }
    },
    [addLine, sendScreenEvent, formMeta],
  );

  const dismissReview = useCallback(() => {
    setReviewDraft(null);
    sendScreenEvent('The customer closed the review screen to keep talking.');
  }, [sendScreenEvent]);

  // ---- session lifecycle ----------------------------------------------------

  // Deliberately not async at the top: the <audio> element must be created and
  // .play()'d synchronously inside the user's tap gesture so mobile Safari
  // permits remote playback once the WebRTC track arrives.
  const start = useCallback(() => {
    if (startingRef.current || status === 'live' || status === 'connecting') return;
    if (!formId) {
      setError('Missing form.');
      setStatus('error');
      return;
    }
    if (!userId) {
      setError('Still loading — try again in a moment.');
      setStatus('error');
      return;
    }

    startingRef.current = true;
    const gen = ++genRef.current;
    setError(null);
    setDraft({});
    setTranscript([]);
    setReviewDraft(null);
    setLinkOffer(null);
    setShownImage(null);
    setCameraRequest(null);
    setThinking(false);
    pendingPhotoRef.current = null;
    setSubmitted(false);
    setKeepOpen(false);
    setStatus('connecting');

    // Unlock audio playback within the tap gesture (see note above).
    const audioEl = document.createElement('audio');
    audioEl.autoplay = true;
    audioEl.setAttribute('playsinline', 'true');
    audioRef.current = audioEl;
    audioEl.play().catch(() => { /* no source yet — the gesture still unlocks it */ });

    (async () => {
      // Resources are tracked in these locals (not just the refs) so that if
      // stop()/cleanup() ran mid-await — clearing the refs and bumping the
      // generation — the bail-out path can still release everything created
      // by this (now cancelled) attempt. Refs may already belong to a newer
      // session, so the bail-out only ever touches its own locals.
      let pc: RTCPeerConnection | null = null;
      let mic: MediaStream | null = null;
      const cancelled = () => genRef.current !== gen;
      const bail = () => {
        try { pc?.close(); } catch { /* noop */ }
        mic?.getTracks().forEach((t) => t.stop());
        audioEl.pause();
        audioEl.srcObject = null;
        audioEl.remove();
      };
      try {
        // 1. Mint the session from our backend. The session's identity comes
        // from the verified Firebase ID token — the server never trusts a
        // client-claimed userId (audit P1-1).
        const idToken = await AuthService.getInstance().getIdToken();
        const sResp = await fetch('/api/voice/session', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ formId, idToken, clientSurface: 'web' }),
        });
        if (!sResp.ok) {
          const detail = await sResp.json().catch(() => ({} as any));
          throw new Error(detail?.error || `Voice session request failed (${sResp.status}).`);
        }
        const data: SessionResponse = await sResp.json();
        if (cancelled()) { bail(); return; }
        if (!data.voiceSessionId) {
          throw new Error('Voice session response was incomplete.');
        }
        sessionIdRef.current = data.voiceSessionId;
        setFields(Array.isArray(data.fields) ? data.fields : []);
        if (data.formMeta) setFormMeta(data.formMeta);

        // 2. WebRTC: our server does the SDP exchange with OpenAI.
        pc = new RTCPeerConnection();
        pcRef.current = pc;
        pc.ontrack = (e) => {
          audioEl.srcObject = e.streams[0];
          audioEl.play().catch(() => { /* already unlocked by the gesture */ });
        };
        // Dead call detection: if the connection drops mid-call, stop cleanly
        // and surface an error instead of sitting at "Live" until the timer.
        pc.onconnectionstatechange = () => {
          if (cancelled() || !pc) return;
          const state = pc.connectionState;
          if (state === 'failed' || state === 'disconnected' || state === 'closed') {
            cleanup();
            setError('Connection lost.');
            setStatus('error');
          }
        };

        mic = await navigator.mediaDevices.getUserMedia({ audio: true });
        if (cancelled()) { bail(); return; }
        streamRef.current = mic;
        mic.getTracks().forEach((track) => pc!.addTrack(track, mic!));

        // The data channel must exist before the offer is created.
        const dc = pc.createDataChannel('oai-events');
        dcRef.current = dc;
        dc.onmessage = onDataChannelMessage;
        // Status flips to 'live' on session.started (the model speaks first
        // on its own — the prompt tells it to open with the disclosure).

        const offer = await pc.createOffer();
        if (cancelled()) { bail(); return; }
        await pc.setLocalDescription(offer);
        if (cancelled()) { bail(); return; }

        const sdpResp = await fetch(data.webrtcUrl || '/api/voice/webrtc', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ voiceSessionId: data.voiceSessionId, sdp: offer.sdp }),
        });
        if (!sdpResp.ok) {
          const detail = await sdpResp.json().catch(() => ({} as any));
          throw new Error(detail?.error || `Voice connect failed (${sdpResp.status}).`);
        }
        const answer = await sdpResp.json();
        if (cancelled()) { bail(); return; }
        if (!answer?.sdp) throw new Error('Voice connect returned no answer.');
        await pc.setRemoteDescription({ type: 'answer', sdp: answer.sdp });
        if (cancelled()) { bail(); return; }

        // 3. Cost guardrail: client-side hangup at the server-provided cap.
        const maxMs = (data.maxSeconds || 600) * 1000;
        timerRef.current = window.setTimeout(() => stopRef.current(), maxMs);
        startingRef.current = false;
      } catch (e: any) {
        if (cancelled()) { bail(); return; }
        cleanup();
        setError(e?.message || 'Could not start voice.');
        setStatus('error');
      }
    })();
  }, [formId, userId, status, onDataChannelMessage, cleanup]);

  return {
    status,
    error,
    fields,
    draft,
    transcript,
    reviewDraft,
    submitted,
    keepOpen,
    formMeta,
    linkOffer,
    shownImage,
    closeShownImage: () => setShownImage(null),
    disclosure: formMeta?.voiceDisclosure || DEFAULT_DISCLOSURE,
    cameraRequest,
    completePhotoCapture,
    cancelPhotoCapture,
    thinking,
    start,
    stop,
    submitReview,
    dismissReview,
  };
}

export default useVoiceSession;
